import { useCallback, useEffect, useRef, useState } from 'react'

/** How long «پرداخت» stays down after the gateway request has left. */
export const GATEWAY_HANDOFF_MS = 15_000

/**
 * The lock on a pay button from the moment a gateway request leaves until the
 * browser has actually gone to the bank.
 *
 * `useMutation`'s `isLoading` covers the request alone. The instant the 201
 * lands it is false again, while `window.location.replace(payment_link)` is
 * still resolving — DNS, TLS, the edge, the bank's own page — which on a phone
 * network takes seconds. For those seconds the button is live and the screen
 * looks untouched, so a payer who taps again sends a second request: a second
 * purchase registered at the bank, and a second `replace()` that cancels the
 * navigation the first one had started. On 2026-09-17 one payer tapped ten
 * times inside two seconds; Pasargad registered ten purchases and the phone
 * reached the tenth.
 *
 * - `lock()` as the request leaves. `isLocked()` reads a ref, so a second tap
 *   in the same tick — before React has re-rendered `disabled` — is refused.
 * - `release()` on error: the message is on screen and the payer must be able
 *   to act on it at once.
 * - `hold()` on success, right before the redirect: the button stays down for
 *   `GATEWAY_HANDOFF_MS` and then lets go by itself, so a navigation that never
 *   happens leaves a screen the payer can still use.
 */
export default function useGatewayHandoff(holdMs: number = GATEWAY_HANDOFF_MS) {
    const [locked, setLocked] = useState(false)
    const lockedRef = useRef(false)
    const timer = useRef<number | undefined>(undefined)

    const clearTimer = useCallback(() => {
        if (timer.current !== undefined) {
            window.clearTimeout(timer.current)
            timer.current = undefined
        }
    }, [])

    const set = useCallback((value: boolean) => {
        lockedRef.current = value
        setLocked(value)
    }, [])

    const lock = useCallback(() => {
        clearTimer()
        set(true)
    }, [clearTimer, set])

    const release = useCallback(() => {
        clearTimer()
        set(false)
    }, [clearTimer, set])

    const hold = useCallback(() => {
        clearTimer()
        set(true)
        timer.current = window.setTimeout(() => {
            timer.current = undefined
            set(false)
        }, holdMs)
    }, [clearTimer, set, holdMs])

    const isLocked = useCallback(() => lockedRef.current, [])

    useEffect(() => clearTimer, [clearTimer])

    return { locked, isLocked, lock, hold, release }
}
